import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api from '../services/api';
import { groupLogicalInventory } from '../utils/logicalInventory';
import haptic from '../utils/haptic';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/roles';
import NoteEditor from '../components/NoteEditor';
import InlineConsolidator from '../components/InlineConsolidator';
import StockChip from '../components/StockChip';
import LogisticsModal from '../components/LogisticsModal';
import './ChecklistPage.css';

function IconPencil() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}
// Ícono chico de vínculo (cadena) para equipos vinculados entre eventos.
function LinkGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, verticalAlign: '-1px' }} aria-hidden="true">
      <path d="M9 17H7A5 5 0 0 1 7 7h2" /><path d="M15 7h2a5 5 0 0 1 0 10h-2" /><line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}
// Badge "se queda para / viene de" en carga y descarga.
function LinkStayBadge({ text }) {
  return (
    <div style={{
      marginTop: 3, display: 'inline-flex', alignItems: 'center', gap: 4,
      maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      fontSize: 10.5, color: 'var(--link-badge, #6366f1)',
    }} title={text}>
      <LinkGlyph /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
    </div>
  );
}

export default function ChecklistPage({ checklistId: propId, panelMode = false, onTabSwitch }) {
  const { checklistId: paramId } = useParams();
  const checklistId = propId || paramId;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [checklist, setChecklist] = useState(null);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteTarget, setNoteTarget] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [localCategories, setLocalCategories] = useState([]);
  const [newCatTarget, setNewCatTarget] = useState(null);
  const [newCatValue, setNewCatValue] = useState('');
  const newCatInputRef = useRef(null);
  const [localZones, setLocalZones] = useState([]);
  const [newZoneInput, setNewZoneInput] = useState(false);
  const [newZoneValue, setNewZoneValue] = useState('');
  const newZoneInputRef = useRef(null);
  const [addInlineTarget, setAddInlineTarget] = useState(null);
  const [addInlineValue, setAddInlineValue] = useState('');
  const addInlineRef = useRef(null);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [sectionRenameTarget, setSectionRenameTarget] = useState(null); // { kind, zone, category, isLocal }
  const [sectionRenameValue, setSectionRenameValue] = useState('');
  const sectionRenameRef = useRef(null);
  const [isConnected, setIsConnected] = useState(true);
  const [links, setLinks] = useState({});
  const [groupCtx, setGroupCtx] = useState({}); // vínculos de equipos entre eventos
  const [partialLoadError, setPartialLoadError] = useState(''); // cargas accesorias caídas
  const [transfers, setTransfers] = useState([]);
  const [showLogisticsModal, setShowLogisticsModal] = useState(false);
  const [cargaItems, setCargaItems] = useState([]);
  const [cargaId, setCargaId] = useState(null);
  const [eventLocation, setEventLocation] = useState(null);
  const [descargarChecked, setDescargarChecked] = useState({});
  const [transferOutMap, setTransferOutMap] = useState({});
  const [transferInMap, setTransferInMap] = useState({});
  const [siblingId, setSiblingId] = useState(null);
  const [activeItemIndex, setActiveItemIndex] = useState(-1);
  const [alreadyDispatchedMap, setAlreadyDispatchedMap] = useState({});
  const [descargarVehicle, setDescargarVehicle] = useState('');
  const [descargarDestinoId, setDescargarDestinoId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [descargarSaving, setDescargarSaving] = useState(false);
  const [descargarError, setDescargarError] = useState('');
  const [event, setEvent] = useState(null);
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [conflictsData, setConflictsData] = useState(null);
  const socketRef = useRef(null);
  const itemRefs = useRef({});

  const canOperate = canWrite(user, 'agenda.equipos.evento');
  const canManageLogistics = canWrite(user, 'logistica');

  const committedIds = useMemo(() => {
    const ids = new Set();
    for (const itemLinks of Object.values(links)) {
      for (const l of itemLinks) {
        if (l.inventory_item_id) ids.add(l.inventory_item_id);
      }
    }
    return ids;
  }, [links]);

  const crossConflicts = useMemo(() => {
    if (!conflictsData?.by_name) return {};
    const result = {};
    for (const [name, c] of Object.entries(conflictsData.by_name)) {
      if ((c.soft_conflict || 0) + (c.en_uso || 0) > 0)
        result[name] = (c.soft_conflict || 0) + (c.en_uso || 0);
    }
    return result;
  }, [conflictsData]);

  const load = useCallback(async () => {
    try {
      const checklistRes = await api.get(`/checklists/${checklistId}`);
      const data = checklistRes.data;

      // El evento es parte del núcleo (el render lo asume); el resto degrada:
      // si una carga accesoria falla, la página se muestra igual y se avisa.
      // Antes un solo 500 de cualquiera dejaba el checklist en blanco.
      const eventRes = await api.get(`/events/${data.event_id}`);

      const extraRequests = [
        api.get(`/links/checklist/${checklistId}`),
        data.type === 'CARGA'
          ? api.get(`/checklists/${checklistId}/logistics`)
          : Promise.resolve({ data: [] }),
        api.get(`/checklists/${checklistId}/carga-items`),
        api.get(`/checklists/event/${data.event_id}`),
        api.get(`/staff-assignments?event_id=${data.event_id}`),
        api.get(`/links/group-context?event_id=${data.event_id}`),
      ];
      const [linksRes, logisticsRes, cargaItemsRes, eventCLRes, staffRes, groupCtxRes] =
        await Promise.allSettled(extraRequests);
      const failed = [];
      const val = (r, label, fallback) => {
        if (r.status === 'fulfilled') return r.value.data;
        failed.push(label);
        return fallback;
      };

      setGroupCtx(val(groupCtxRes, 'vínculos entre eventos', {}) || {});

      setChecklist(data);
      setEvent(eventRes.data);
      setStaffAssignments(val(staffRes, 'personal asignado', []) || []);
      setItems(data.items || []);
      setNotes(data.notes || []);
      setLinks(val(linksRes, 'equipos asignados', []));
      setTransfers(val(logisticsRes, 'logísticas', []));
      const sibling = (val(eventCLRes, 'checklist hermano', []) || []).find(c => c.type !== data.type);
      setSiblingId(sibling?.id || null);

      const cargaData = val(cargaItemsRes, 'ítems de carga', { items: [] }) || { items: [] };
      setPartialLoadError(failed.length ? `No se pudo cargar: ${failed.join(', ')}. El resto de la página funciona.` : '');
      setCargaId(cargaData.carga_id);
      setCargaItems(cargaData.items || []);
      setEventLocation(cargaData.event_location || null);
      if (data.type === 'DESCARGA') {
        const initial = {};
        for (const item of (cargaData.items || [])) {
          initial[item.id] = !!item.already_received && !!item.is_dischargeable;
        }
        setDescargarChecked(initial);
      }
      const tfOutMap = {};
      const tfInMap = {};
      const dispMap = {};
      for (const item of (cargaData.items || [])) {
        if (item.transfer_out) tfOutMap[item.id] = item.transfer_out;
        if (item.transfer_in) tfInMap[item.id] = item.transfer_in;
        if (item.already_dispatched) dispMap[item.id] = true;
      }
      setTransferOutMap(tfOutMap);
      setTransferInMap(tfInMap);
      setAlreadyDispatchedMap(dispMap);

      if (data.type === 'CARGA') {
        api.get(`/inventory/event-conflicts?event_id=${data.event_id}`)
          .then(r => setConflictsData(r.data))
          .catch(() => {});
      }

    } catch (e) {
      if (e.response?.status === 404 && !panelMode) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [checklistId, navigate, panelMode]);

  const refreshConflictsData = useCallback(() => {
    if (!checklist?.event_id) return;
    api.get(`/inventory/event-conflicts?event_id=${checklist.event_id}`)
      .then(r => setConflictsData(r.data))
      .catch(() => {});
  }, [checklist?.event_id]);

  useEffect(() => { load(); }, [load]);

  // Socket.io
  useEffect(() => {
    const socket = io('/', { withCredentials: true });
    socketRef.current = socket;

    socket.emit('checklist:join', checklistId);

    socket.on('item:updated', ({ item }) => {
      setItems(prev => prev.map(i => i.id === item.id ? item : i));
    });

    socket.on('note:updated', ({ note }) => {
      setNotes(prev => {
        const exists = prev.find(n => n.id === note.id);
        return exists
          ? prev.map(n => n.id === note.id ? note : n)
          : [...prev, note];
      });
    });

    socket.on('note:deleted', ({ zone, category }) => {
      setNotes(prev => prev.filter(n =>
        !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
      ));
    });

    socket.on('checklist:status', ({ status }) => {
      setChecklist(prev => ({ ...prev, status }));
    });

    socket.on('item:added', ({ item }) => {
      setItems(prev => [...prev, item]);
    });

    socket.on('item:deleted', ({ itemId }) => {
      setItems(prev => prev.filter(i => i.id !== itemId));
    });

    socket.on('checklist:reset', () => {
      load();
    });

    socket.on('section:renamed', () => {
      load();
    });

    socket.on('section:deleted', () => {
      load();
    });

    socket.on('disconnect', () => setIsConnected(false));
    socket.on('connect', () => setIsConnected(true));

    return () => {
      // Al cambiar de checklist (ej. carga→descarga) el efecto se re-ejecuta y
      // desconecta este socket. Sacamos el handler de 'disconnect' antes para
      // no disparar el banner "Sin conexión" en una desconexión intencional.
      socket.off('disconnect');
      socket.emit('checklist:leave', checklistId);
      socket.disconnect();
    };
  }, [checklistId]);

  const toggleItem = async (item) => {
    if (!canOperate || !isConnected) return;
    const newChecked = !item.is_checked;
    // El pulso va enseguida, con el tilde optimista: cargando el camión la
    // confirmación tiene que llegar cuando tocás, no cuando responde la red.
    if (newChecked) haptic.tick(); else haptic.untick();
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_checked: newChecked } : i));
    try {
      await api.patch(`/checklists/${checklistId}/items/${item.id}`, {
        is_checked: newChecked
      });
    } catch (e) {
      console.error(e);
      // Se deshizo el tilde: el doble pulso avisa que no quedó guardado, que es
      // justo lo que no se ve si no estás mirando la pantalla.
      haptic.warn();
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, is_checked: item.is_checked } : i));
    }
  };

  const handleReplace = async (item, newName) => {
    await api.patch(`/checklists/${checklistId}/items/${item.id}`, { current_name: newName });
    setItems(prev => prev.map(i =>
      i.id === item.id ? { ...i, current_name: newName, is_replaced: newName !== i.original_name } : i
    ));
  };

  const handleNote = async (zone, category, note) => {
    try {
      if (note === '') {
        await api.delete(`/checklists/${checklistId}/notes`, { data: { zone, category } });
        setNotes(prev => prev.filter(n =>
          !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
        ));
      } else {
        const res = await api.put(`/checklists/${checklistId}/notes`, { zone, category, note });
        setNotes(prev => {
          const exists = prev.find(n =>
            (n.zone || null) === (zone || null) && (n.category || null) === (category || null)
          );
          return exists
            ? prev.map(n =>
                (n.zone || null) === (zone || null) && (n.category || null) === (category || null)
                  ? res.data : n
              )
            : [...prev, res.data];
        });
      }
      setNoteTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteNote = async (zone, category) => {
    try {
      await api.delete(`/checklists/${checklistId}/notes`, { data: { zone, category } });
      setNotes(prev => prev.filter(n =>
        !((n.zone || null) === (zone || null) && (n.category || null) === (category || null))
      ));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAddItem = async (zone, category, name) => {
    try {
      await api.post(`/checklists/${checklistId}/items`, { zone, category, name });
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteItem = (itemId, itemName) => {
    setDeleteItemTarget({ id: itemId, name: itemName });
  };

  const confirmDeleteItem = async () => {
    if (!deleteItemTarget) return;
    try {
      await api.delete(`/checklists/${checklistId}/items/${deleteItemTarget.id}`);
      setItems(prev => prev.filter(i => i.id !== deleteItemTarget.id));
      setDeleteItemTarget(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
      setDeleteItemTarget(null);
    }
  };

  const startSectionRename = (kind, zone, category, isLocal, currentName) => {
    setSectionRenameTarget({ kind, zone, category, isLocal });
    setSectionRenameValue(currentName || '');
  };

  const commitSectionRename = async () => {
    const target = sectionRenameTarget;
    if (!target) return;
    const name = sectionRenameValue.trim();
    setSectionRenameTarget(null);
    setSectionRenameValue('');
    if (!name) return;
    const { kind, zone, category, isLocal } = target;

    // Un sector puede estar en dos lados a la vez: guardado en la DB y además
    // en el estado local que lo dibujó cuando todavía no tenía contenido.
    // Tocar uno solo deja que el otro siga pintando el nombre viejo, así que
    // el estado local se actualiza siempre, sea o no un sector persistido.
    if (kind === 'zone') {
      setLocalZones(prev => prev.map(z => z === zone ? name : z));
      // Las categorías locales cuelgan del NOMBRE del sector, no de un id: si
      // no se renombran también, quedan apuntando al anterior y lo reviven.
      setLocalCategories(prev => prev.map(c =>
        (c.zone || null) === (zone || null) ? { ...c, zone: name } : c
      ));
    } else {
      setLocalCategories(prev => prev.map(c =>
        (c.zone || null) === (zone || null) && (c.category || null) === (category || null)
          ? { ...c, category: name }
          : c
      ));
    }

    if (isLocal) return;

    try {
      await api.put(`/checklists/${checklistId}/sections/rename`, { kind, zone, category, newName: name });
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const cancelSectionRename = () => {
    setSectionRenameTarget(null);
    setSectionRenameValue('');
  };

  const handleSectionDelete = async (kind, zone, category, isLocal, label) => {
    const consequence = kind === 'zone'
      ? 'Los equipos pasan a GENERAL / quedan sin sector; la nota de la sección se elimina.'
      : 'Los equipos quedan sin categoría; la nota de la sección se elimina.';
    if (!window.confirm(`¿Eliminar "${label}"?\n\n${consequence}`)) return;

    // Mismo caso que en el renombre: el bloque puede estar guardado en la DB y
    // a la vez en el estado local. Borrar sólo la copia persistida hace que la
    // local lo vuelva a pintar vacío, y parece que el borrado no funcionó hasta
    // recargar. Al borrar un sector se van también sus categorías locales, que
    // si no lo resucitan solas.
    if (kind === 'zone') {
      setLocalZones(prev => prev.filter(z => z !== zone));
      setLocalCategories(prev => prev.filter(c => (c.zone || null) !== (zone || null)));
    } else {
      setLocalCategories(prev => prev.filter(c =>
        !((c.zone || null) === (zone || null) && (c.category || null) === (category || null))
      ));
    }

    if (isLocal) return;

    try {
      await api.delete(`/checklists/${checklistId}/sections`, { data: { kind, zone, category } });
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleReset = async () => {
    try {
      await api.post(`/checklists/${checklistId}/reset`);
      load();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleTabSwitch = async (targetType) => {
    if (!checklist || targetType === checklist.type) return;
    if (siblingId) {
      if (targetType === 'DESCARGA') {
        await api.post(`/checklists/${siblingId}/sync-from-carga`).catch(console.error);
      }
      if (panelMode && onTabSwitch) onTabSwitch(siblingId);
      else navigate(`/checklist/${siblingId}`);
    } else if (canOperate) {
      try {
        const { data } = await api.post('/checklists', { event_id: checklist.event_id, type: targetType });
        if (targetType === 'DESCARGA') {
          await api.post(`/checklists/${data.id}/sync-from-carga`).catch(console.error);
        }
        setSiblingId(data.id);
        if (panelMode && onTabSwitch) onTabSwitch(data.id);
        else navigate(`/checklist/${data.id}`);
      } catch (e) {
        alert('Error: ' + (e.response?.data?.error || e.message));
      }
    }
  };

  const handleDescargarDone = async () => {
    try {
      const cargaItemsRes = await api.get(`/checklists/${checklistId}/carga-items`);
      const cargaData = cargaItemsRes.data;
      setCargaId(cargaData.carga_id);
      setCargaItems(cargaData.items || []);
      setEventLocation(cargaData.event_location || null);
      const initial = {};
      const dispMap = {};
      for (const item of (cargaData.items || [])) {
        initial[item.id] = !!item.already_received && !!item.is_dischargeable;
        if (item.already_dispatched) dispMap[item.id] = true;
      }
      setDescargarChecked(initial);
      setAlreadyDispatchedMap(dispMap);
    } catch (e) {
      console.error(e);
    }
  };

  const refreshDispatchState = useCallback(async () => {
    try {
      const { data } = await api.get(`/checklists/${checklistId}/carga-items`);
      const dispMap = {};
      const tfOutMap = {};
      const tfInMap = {};
      for (const item of (data.items || [])) {
        if (item.already_dispatched) dispMap[item.id] = true;
        if (item.transfer_out) tfOutMap[item.id] = item.transfer_out;
        if (item.transfer_in) tfInMap[item.id] = item.transfer_in;
      }
      setAlreadyDispatchedMap(dispMap);
      setTransferOutMap(tfOutMap);
      setTransferInMap(tfInMap);
    } catch (e) { console.error(e); }
  }, [checklistId]);

  useEffect(() => {
    if (!checklist || checklist.type !== 'DESCARGA' || !canManageLogistics) return;
    api.get('/personnel').then(r => setPersonnel(r.data)).catch(console.error);
    api.get('/locations').then(r => {
      setLocations(r.data);
      const home = r.data.find(l => l.is_home);
      // Por defecto el destino es el depósito home, pero se puede cambiar.
      if (home) setDescargarDestinoId(prev => prev ?? home.id);
    }).catch(console.error);
  }, [checklist?.type, canManageLogistics]); // eslint-disable-line

  const handleDescargarCreate = async () => {
    if (!eventLocation?.id) return;
    // Destino: el elegido, o el depósito home por defecto.
    const toLocationId = descargarDestinoId || locations.find(l => l.is_home)?.id;
    if (!toLocationId) { setDescargarError('No se encontró el destino'); return; }
    setDescargarSaving(true); setDescargarError('');
    let dispatched = false;
    try {
      const transferRes = await api.post('/transfers', {
        from_location_id: eventLocation.id,
        to_location_id: toLocationId,
        vehicle: descargarVehicle || null,
        event_id: checklist.event_id,
      });
      const transferId = transferRes.data.id;

      const toReturn = descargableItems.filter(i => descargarChecked[i.id] && !i.already_received);
      for (const item of toReturn) {
        const itemLinks = item.links || [];
        if (itemLinks.length === 0) {
          if (!item.current_name?.trim()) continue;
          await api.post(`/transfers/${transferId}/items`, { checklist_item_name: item.current_name });
        } else {
          const addedPacks = new Set();
          for (const link of itemLinks) {
            if (link.pack_id && !addedPacks.has(link.pack_id)) {
              addedPacks.add(link.pack_id);
              await api.post(`/transfers/${transferId}/items`, { pack_id: link.pack_id });
            } else if (link.inventory_item_id && !link.pack_id) {
              await api.post(`/transfers/${transferId}/items`, { inventory_item_id: link.inventory_item_id });
            }
          }
        }
      }

      await api.patch(`/transfers/${transferId}/dispatch`);
      dispatched = true;
    } catch (e) {
      setDescargarError(e.response?.data?.error || 'Error al crear la logística');
    } finally {
      setDescargarSaving(false);
    }
    // El transfer ya se despachó en el backend: la limpieza de UI corre FUERA
    // del try para que un error acá no se muestre como "falló la logística" ni
    // habilite un reintento que duplicaría el transfer.
    if (dispatched) {
      setDescargarVehicle('');
      await handleDescargarDone();
    }
  };

  const isDescarga = checklist?.type === 'DESCARGA';
  const displayItems = isDescarga ? cargaItems : items;
  const grouped = useMemo(() => {
    const base = groupItems(displayItems);
    for (const { zone, category } of localCategories) {
      const zKey = zone || '';
      let zEntry = base.find(z => (z.zone || '') === zKey);
      if (!zEntry) { zEntry = { zone: zone || null, categories: [] }; base.push(zEntry); }
      const exists = zEntry.categories.some(c => (c.category || '') === (category || ''));
      if (!exists) zEntry.categories.push({ category: category || null, items: [] });
    }
    for (const zone of localZones) {
      const zKey = zone || '';
      const exists = base.some(z => (z.zone || '') === zKey);
      if (!exists) base.push({ zone: zone || null, categories: [] });
    }
    // El resto de los sectores conserva el orden en que vienen los ítems del
    // backend; faltantes se saltea la fila y va siempre primero.
    const fi = base.findIndex(z => (z.zone || '').toUpperCase() === 'FALTANTES');
    if (fi > 0) base.unshift(base.splice(fi, 1)[0]);
    return base;
  }, [displayItems, localCategories, localZones]);

  const flatItems = useMemo(
    () => isDescarga ? [] : grouped.flatMap(z => z.categories.flatMap(c => c.items)),
    [grouped, isDescarga]
  );

  const noContent = grouped.length === 0;

  useEffect(() => {
    if (newCatTarget !== null && newCatInputRef.current) newCatInputRef.current.focus();
  }, [newCatTarget]);

  useEffect(() => {
    if (newZoneInput && newZoneInputRef.current) newZoneInputRef.current.focus();
  }, [newZoneInput]);

  useEffect(() => {
    if (addInlineTarget !== null && addInlineRef.current) addInlineRef.current.focus();
  }, [addInlineTarget]);

  useEffect(() => {
    if (sectionRenameTarget && sectionRenameRef.current) {
      sectionRenameRef.current.focus();
      sectionRenameRef.current.select();
    }
  }, [sectionRenameTarget]);

  const commitNewCategory = (zone) => {
    const name = newCatValue.trim();
    if (name) setLocalCategories(prev => [...prev, { zone: zone || null, category: name }]);
    setNewCatTarget(null);
    setNewCatValue('');
  };

  const commitAddInline = async (zone, category) => {
    const name = addInlineValue.trim();
    setAddInlineTarget(null);
    setAddInlineValue('');
    if (name) await handleAddItem(zone, category, name);
  };

  const commitNewZone = () => {
    const name = newZoneValue.trim();
    if (name) setLocalZones(prev => [...prev, name]);
    setNewZoneInput(false);
    setNewZoneValue('');
  };

  // "GENERAL" = zone null + category null, la misma representación que ya
  // usa el render para equipos sin sector/categoría asignados.
  const GENERAL_KEY = '|';

  // Los faltantes no son una entidad nueva: son un sector con nombre reservado,
  // así que heredan tal cual el alta, el renombre, el borrado y la persistencia
  // del resto. Lo único propio es que se fija arriba de todo y se pinta distinto.
  // La comparación va en mayúsculas para que renombrarlo a "Faltantes" no le
  // saque el resaltado y parezca que se rompió.
  const FALTANTES_ZONE = 'FALTANTES';
  const isFaltantesZone = (zone) => (zone || '').toUpperCase() === FALTANTES_ZONE;

  const openGeneralAdd = () => {
    const existsGeneral = grouped.some(z =>
      (z.zone || '') === '' && z.categories.some(c => (c.category || '') === '')
    );
    if (!existsGeneral) setLocalCategories(prev => [...prev, { zone: null, category: null }]);
    setAddInlineTarget(GENERAL_KEY);
    setAddInlineValue('');
  };

  const openRootCategoryAdd = () => {
    const existsRootZone = grouped.some(z => (z.zone || '') === '');
    if (!existsRootZone) setLocalZones(prev => [...prev, null]);
    setNewCatTarget('');
    setNewCatValue('');
  };

  const openCategoryForLastZone = () => {
    const lastZone = grouped.length > 0 ? grouped[grouped.length - 1].zone : null;
    setNewCatTarget(lastZone || '');
    setNewCatValue('');
  };

  const openNewZone = () => {
    setNewZoneInput(true);
    setNewZoneValue('');
  };

  // A diferencia de "+ Sector", que pide el nombre, acá el nombre ya está dado:
  // se crea el bloque y el cursor cae directo en el primer equipo. También se
  // agrega la categoría nula, que es lo que habilita cargar equipos sueltos
  // dentro del sector (un sector sin categorías sólo deja crear categorías).
  const openFaltantesAdd = () => {
    if (!grouped.some(z => isFaltantesZone(z.zone))) {
      setLocalZones(prev => [...prev, FALTANTES_ZONE]);
      setLocalCategories(prev => [...prev, { zone: FALTANTES_ZONE, category: null }]);
    }
    setAddInlineTarget(`${FALTANTES_ZONE}|`);
    setAddInlineValue('');
  };

  useEffect(() => {
    if (isDescarga || !canOperate) return;
    const handler = (e) => {
      // Ceder control si hay dropdown de sugerencias o se está editando un nombre
      if (document.querySelector('.ic-dropdown')) return;
      if (document.activeElement?.classList.contains('cl-item-edit-input')) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveItemIndex(i => Math.min(i + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveItemIndex(i => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isDescarga, canOperate, flatItems.length]);

  useEffect(() => {
    if (activeItemIndex < 0) return;
    const item = flatItems[activeItemIndex];
    if (item && itemRefs.current[item.id]) {
      itemRefs.current[item.id].scrollIntoView({ block: 'nearest' });
    }
  }, [activeItemIndex]); // eslint-disable-line

  const descargableItems = isDescarga
    ? cargaItems.filter(i => i.is_dischargeable && !i.transfer_out)
    : null;
  const checkedCount = isDescarga
    ? descargableItems.filter(i => descargarChecked[i.id] && !i.already_received).length
    : items.filter(i => i.is_checked).length;
  const totalCount = isDescarga ? descargableItems.length : items.length;
  const progress = totalCount > 0 ? Math.round((checkedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!checklist) return null;

  return (
    <div className="cl-layout">
    {!panelMode && <EventSidebar currentId={+checklistId} navigate={navigate} />}
    <div className={`checklist-page${panelMode ? ' checklist-panel' : ''}`}>
      {!isConnected && (
        <div className="cl-disconnected-banner">
          Sin conexión — los cambios no se guardarán
        </div>
      )}
      {partialLoadError && (
        <div className="cl-disconnected-banner" style={{ background: 'var(--yellow-dim)', color: 'var(--yellow)' }}>
          {partialLoadError}
        </div>
      )}
      {/* Header */}
      <div className="cl-header">
        <div className="cl-event-overview">
          <div className="cl-event-identity">
            <div className="cl-header-title">{event?.name || checklist.event_name}</div>
            <span className="cl-event-place">{event?.place || checklist.event_place || 'Sin lugar'}</span>
            <div className="cl-event-meta">
              <span><small>Orden</small>{event?.odoo_code || checklist.event_code || 'Sin número'}</span>
              <span><small>Referente</small>{event?.sales_referent || 'Sin referente'}</span>
            </div>
          </div>
          {(() => {
            const hasStaff = staffAssignments.length > 0;
            const staffFor = (phase) => staffAssignments
              .filter(a => a.phase === phase)
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(a => a.person_name);
            const formatRange = (start, end) => {
              const from = start?.slice?.(0, 5) || '';
              const to = end?.slice?.(0, 5) || '';
              return from || to ? [from, to].filter(Boolean).join(' - ') : 'Sin horario';
            };
            const rows = [
              { value: formatRange(event?.setup_start || checklist.setup_start, event?.setup_end), valueClass: 'cl-header-time', phase: 'armado', label: 'Armado' },
              { value: formatRange(event?.start_time || checklist.event_start_time, event?.end_time || checklist.event_end_time), valueClass: 'cl-header-time', phase: 'evento', label: 'Operación' },
              { value: formatRange(event?.teardown_start, event?.teardown_end), valueClass: 'cl-header-time', phase: 'desarmado', label: 'Desarmado' },
            ];
            return (
              <>
                <div className={`cl-header-info${hasStaff ? ' cl-header-info--team' : ''}`}>
                  {rows.map(r => (
                    <div className="cl-event-summary" key={r.label}>
                      <span className="cl-event-summary-label">{r.label}</span>
                      <span className={r.valueClass}>{r.value}</span>
                      {r.phase && hasStaff && (
                        <span className="cl-team-names">{staffFor(r.phase).join(' · ') || 'Sin asignar'}</span>
                      )}
                    </div>
                  ))}
                </div>
                {event?.notes && <p className="cl-event-notes">{event.notes}</p>}
              </>
            );
          })()}
        </div>
        <div className="cl-header-bottom">
          <div className="cl-tabs">
            <button
              className={`cl-tab${checklist.type === 'CARGA' ? ' active' : ''}`}
              onClick={() => handleTabSwitch('CARGA')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Carga
            </button>
            <button
              className={`cl-tab${checklist.type === 'DESCARGA' ? ' active' : ''}`}
              onClick={() => handleTabSwitch('DESCARGA')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Descarga
            </button>
          </div>
          <div className="cl-header-actions">
            {!panelMode && (
              <button onClick={() => navigate('/')} className="btn btn-ghost btn-sm">
                ← Volver
              </button>
            )}
            {canManageLogistics && checklist.type === 'CARGA' && (
              <button
                onClick={() => setShowLogisticsModal(true)}
                className="btn btn-ghost btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 3h15v13H1z"/>
                  <path d="M16 8h4l3 3v5h-7V8z"/>
                  <circle cx="5.5" cy="18.5" r="1.5"/>
                  <circle cx="18.5" cy="18.5" r="1.5"/>
                </svg>
                Iniciar logística
                {transfers.filter(t => t.status === 'in_transit').length > 0 && (
                  <span style={{
                    background: 'var(--accent)', color: 'var(--bg)',
                    borderRadius: '999px', fontSize: 10, fontWeight: 700,
                    padding: '0px 5px', lineHeight: '16px',
                  }}>
                    {transfers.filter(t => t.status === 'in_transit').length}
                  </span>
                )}
              </button>
            )}
            {canOperate && (
              <button onClick={() => setShowResetConfirm(true)} className="btn btn-ghost btn-sm">
                Reiniciar
              </button>
            )}

          </div>
        </div>
      </div>


      {/* Progress bar */}
      <div className="cl-progress">
        <div className="cl-progress-bar">
          <div className="cl-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <span className="cl-progress-label">
          {checkedCount} / {totalCount} ítems ({progress}%)
        </span>
      </div>

      {/* Inline DESCARGA logistics bar */}
      {isDescarga && canManageLogistics && (
        <div className="cl-descarga-bar">
          <div className="cl-descarga-bar-vehicle">
            <label className="cl-descarga-label">Vehículo</label>
            <select
              className="input"
              value={descargarVehicle}
              onChange={e => setDescargarVehicle(e.target.value)}
              disabled={descargarSaving}
            >
              <option value="">SIN ESPECIFICAR</option>
              {VEHICLES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="cl-descarga-bar-responsible">
            <label className="cl-descarga-label">Destino</label>
            <select
              className="input"
              value={descargarDestinoId ?? ''}
              onChange={e => setDescargarDestinoId(e.target.value ? Number(e.target.value) : null)}
              disabled={descargarSaving}
            >
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}{l.is_home ? ' (depósito)' : ''}</option>
              ))}
            </select>
          </div>
          <div className="cl-descarga-bar-action">
            {descargarError && <div className="cl-descarga-error">{descargarError}</div>}
            <button
              className="btn btn-primary"
              onClick={handleDescargarCreate}
              disabled={descargarSaving || checkedCount === 0 || !eventLocation}
              style={{ whiteSpace: 'nowrap', width: '100%' }}
            >
              {descargarSaving ? 'Creando...' : `Iniciar logística (${checkedCount})`}
            </button>
          </div>
        </div>
      )}

      {/* Groups */}
      <div className="cl-groups">
        {canOperate && !isDescarga && !noContent && (
          <div className="cl-toolbar">
            <button className="btn btn-primary btn-sm" onClick={openGeneralAdd}>+ Equipo</button>
            <button className="btn btn-ghost btn-sm" onClick={openCategoryForLastZone}>+ Categoría</button>
            <button className="btn btn-ghost btn-sm" onClick={openNewZone}>+ Sector</button>
            <button className="btn btn-sm cl-btn-faltantes" onClick={openFaltantesAdd}>+ Faltantes</button>
          </div>
        )}
        {noContent && (
          <div className="cl-empty-state">
            <div className="cl-empty-title">Este evento todavía no tiene equipos</div>
            {canOperate && !isDescarga ? (
              <>
                <p className="cl-empty-subtitle">Agregá el primer equipo, o empezá organizando por categorías y sectores.</p>
                <div className="cl-empty-actions">
                  <button className="btn btn-primary btn-sm" onClick={openGeneralAdd}>+ Equipo</button>
                  <button className="btn btn-ghost btn-sm" onClick={openRootCategoryAdd}>+ Categoría</button>
                  <button className="btn btn-ghost btn-sm" onClick={openNewZone}>+ Sector</button>
                  <button className="btn btn-sm cl-btn-faltantes" onClick={openFaltantesAdd}>+ Faltantes</button>
                </div>
              </>
            ) : (
              <p className="cl-empty-subtitle">Sin equipos cargados.</p>
            )}
          </div>
        )}
        {grouped.map((zoneGroup, zi) => {
          const zoneIsLocal = zoneGroup.categories.every(c => c.items.length === 0) && localZones.includes(zoneGroup.zone);
          const zoneEditable = canOperate && !isDescarga && !!zoneGroup.zone;
          const isRenamingZone = sectionRenameTarget?.kind === 'zone'
            && (sectionRenameTarget.zone || null) === (zoneGroup.zone || null);
          return (
          <div key={zi} className={`cl-zone-block${isFaltantesZone(zoneGroup.zone) ? ' cl-zone-faltantes' : ''}`}>
            <div className="cl-zone-title">
              {isRenamingZone ? (
                <input
                  ref={sectionRenameRef}
                  className="cl-section-rename-input"
                  value={sectionRenameValue}
                  onChange={e => setSectionRenameValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); commitSectionRename(); }
                    if (e.key === 'Escape') { e.preventDefault(); cancelSectionRename(); }
                  }}
                  onBlur={commitSectionRename}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <span>{zoneGroup.zone || 'GENERAL'}</span>
              )}
              {zoneEditable && !isRenamingZone && (
                <div className="cl-section-actions">
                  <button
                    className="cl-section-action-btn"
                    title="Renombrar sector"
                    aria-label="Renombrar sector"
                    onClick={() => startSectionRename('zone', zoneGroup.zone, null, zoneIsLocal, zoneGroup.zone)}
                  ><IconPencil /></button>
                  <button
                    className="cl-section-action-btn"
                    title="Eliminar sector"
                    aria-label="Eliminar sector"
                    onClick={() => handleSectionDelete('zone', zoneGroup.zone, null, zoneIsLocal, zoneGroup.zone)}
                  ><IconTrash /></button>
                </div>
              )}
            </div>
            <div className="cl-categories">
              {zoneGroup.categories.map((catGroup, ci) => {
                const note = notes.find(n =>
                  (n.zone || null) === (zoneGroup.zone || null) &&
                  (n.category || null) === (catGroup.category || null)
                );
                const isGeneralSection = !zoneGroup.zone && !catGroup.category;
                const catEditable = canOperate && !isDescarga && !isGeneralSection;
                const catIsLocal = catGroup.items.length === 0 && localCategories.some(c =>
                  (c.zone || null) === (zoneGroup.zone || null) && (c.category || null) === (catGroup.category || null)
                );
                const isRenamingCat = sectionRenameTarget?.kind === 'category'
                  && (sectionRenameTarget.zone || null) === (zoneGroup.zone || null)
                  && (sectionRenameTarget.category || null) === (catGroup.category || null);
                const catLabel = catGroup.category || (zoneGroup.zone ? 'General' : '');
                return (
                  <div
                    key={ci}
                    className={`cl-category-block${ci < zoneGroup.categories.length - 1 ? ' cl-category-sep' : ''}`}
                  >
                    <div className="cl-category-header">
                      <div className="cl-category-title-group">
                        {isRenamingCat ? (
                          <input
                            ref={sectionRenameRef}
                            className="cl-section-rename-input"
                            value={sectionRenameValue}
                            onChange={e => setSectionRenameValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); commitSectionRename(); }
                              if (e.key === 'Escape') { e.preventDefault(); cancelSectionRename(); }
                            }}
                            onBlur={commitSectionRename}
                          />
                        ) : (
                          <span className="cl-category-title">
                            {catLabel}
                          </span>
                        )}
                        {catEditable && !isRenamingCat && (
                          <div className="cl-section-actions">
                            <button
                              className="cl-section-action-btn"
                              title="Renombrar categoría"
                              aria-label="Renombrar categoría"
                              onClick={() => startSectionRename('category', zoneGroup.zone, catGroup.category, catIsLocal, catGroup.category)}
                            ><IconPencil /></button>
                            <button
                              className="cl-section-action-btn"
                              title="Eliminar categoría"
                              aria-label="Eliminar categoría"
                              onClick={() => handleSectionDelete('category', zoneGroup.zone, catGroup.category, catIsLocal, catLabel)}
                            ><IconTrash /></button>
                          </div>
                        )}
                      </div>
                      <div className="cl-group-actions">
                        <span className="cl-group-count text-dim text-xs">
                          {catGroup.items.filter(i => i.is_checked).length}/{catGroup.items.length}
                        </span>
                        {canOperate && (
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => setNoteTarget({ zone: zoneGroup.zone, category: catGroup.category, existing: note })}
                          >
                            {note ? '📝 Nota' : '+ Nota'}
                          </button>
                        )}
                      </div>
                    </div>
                    {note && (
                      <div
                        className="cl-note"
                        onClick={() => canOperate && setNoteTarget({ zone: zoneGroup.zone, category: catGroup.category, existing: note })}
                      >
                        📝 {note.note}
                      </div>
                    )}
                    <div className="cl-items">
                      {isDescarga && <DescargaHeader />}
                      {!isDescarga && canOperate && (
                        <div className="cl-items-table-head" aria-hidden="true">
                          <span>OK</span>
                          <span>Equipo elegido</span>
                          <span>Equipo esperado</span>
                          <span />
                        </div>
                      )}
                      {catGroup.items.map(item => isDescarga ? (
                        <DescargaItem
                          key={item.id}
                          item={item}
                          canOperate={canOperate}
                          isChecked={!!descargarChecked[item.id]}
                          onToggle={() => {
                            const willCheck = !descargarChecked[item.id];
                            const stays = groupCtx[item.id]?.stays_for;
                            if (willCheck && stays && !window.confirm(
                              `"${item.current_name}" se queda para ${stays.event_name}.\n\n¿Seguro que lo querés devolver al depósito? Al hacerlo se romperá el vínculo con ese evento.`
                            )) return;
                            if (willCheck) haptic.tick(); else haptic.untick();
                            setDescargarChecked(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                          }}
                          linkCtx={groupCtx[item.id]}
                        />
                      ) : (
                        <CheckItem
                          key={item.id}
                          item={item}
                          canOperate={canOperate}
                          isCarga={true}
                          itemLinks={links[item.id] || []}
                          transferOut={transferOutMap[item.id] || null}
                          transferIn={transferInMap[item.id] || null}
                          alreadyDispatched={alreadyDispatchedMap[item.id] || false}
                          isActive={flatItems.indexOf(item) === activeItemIndex}
                          itemRef={node => { itemRefs.current[item.id] = node; }}
                          onToggle={() => toggleItem(item)}
                          onConsolidate={(newLinks) => { setLinks(prev => ({ ...prev, [item.id]: newLinks })); refreshConflictsData(); }}
                          onRename={(newName) => handleReplace(item, newName)}
                          onDelete={handleDeleteItem}
                          onActivate={() => setActiveItemIndex(flatItems.indexOf(item))}
                          committedIds={committedIds}
                          crossConflicts={crossConflicts}
                          conflictsData={conflictsData}
                          eventId={checklist?.event_id}
                          linkCtx={groupCtx[item.id]}
                          conflictData={(() => {
                            if (!conflictsData?.by_name) return null;
                            const names = [...new Set((links[item.id] || []).map(l => l.item_name).filter(Boolean))];
                            let worst = null;
                            for (const name of names) {
                              const c = conflictsData.by_name[name];
                              if (!c || (!c.en_uso && !c.soft_conflict)) continue;
                              if (!worst || c.en_uso > worst.en_uso) worst = { ...c, item_name: name };
                            }
                            return worst;
                          })()}
                          onConflictEventClick={async (c) => {
                            try {
                              const { data: cls } = await api.get(`/checklists/event/${c.event_id}`);
                              const carga = cls.find(cl => cl.type === 'CARGA');
                              if (carga) navigate(`/checklist/${carga.id}`);
                            } catch {}
                          }}
                        />
                      ))}
                    </div>
                    {canOperate && !isDescarga && (() => {
                      const key = `${zoneGroup.zone || ''}|${catGroup.category || ''}`;
                      const isAdding = addInlineTarget === key;
                      return isAdding ? (
                        <div className="cl-add-item-row">
                          <input
                            ref={addInlineRef}
                            className="cl-add-item-input"
                            placeholder="Nombre del equipo…"
                            value={addInlineValue}
                            onChange={e => setAddInlineValue(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                const name = addInlineValue.trim();
                                if (!name) { setAddInlineTarget(null); setAddInlineValue(''); return; }
                                setAddInlineValue('');
                                handleAddItem(zoneGroup.zone, catGroup.category, name)
                                  .then(() => addInlineRef.current?.focus());
                              }
                              if (e.key === 'Escape') { setAddInlineTarget(null); setAddInlineValue(''); }
                            }}
                            onBlur={() => commitAddInline(zoneGroup.zone, catGroup.category)}
                          />
                        </div>
                      ) : (
                        <button
                          className="cl-add-item-btn"
                          onClick={() => { setAddInlineTarget(key); setAddInlineValue(''); }}
                        >
                          + Equipo
                        </button>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
            {canOperate && !isDescarga && (
              <div className="cl-new-cat">
                {newCatTarget === (zoneGroup.zone || '') ? (
                  <input
                    ref={newCatInputRef}
                    className="cl-new-cat-input"
                    placeholder="Nombre de categoría…"
                    value={newCatValue}
                    onChange={e => setNewCatValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); commitNewCategory(zoneGroup.zone); }
                      if (e.key === 'Escape') { setNewCatTarget(null); setNewCatValue(''); }
                    }}
                    onBlur={() => commitNewCategory(zoneGroup.zone)}
                  />
                ) : (
                  <button
                    className="cl-new-cat-btn"
                    onClick={() => { setNewCatTarget(zoneGroup.zone || ''); setNewCatValue(''); }}
                  >
                    + Nueva categoría
                  </button>
                )}
              </div>
            )}
          </div>
          );
        })}
        {canOperate && !isDescarga && newZoneInput && (
          <div className="cl-new-cat">
            <input
              ref={newZoneInputRef}
              className="cl-new-cat-input"
              placeholder="Nombre del sector…"
              value={newZoneValue}
              onChange={e => setNewZoneValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); commitNewZone(); }
                if (e.key === 'Escape') { setNewZoneInput(false); setNewZoneValue(''); }
              }}
              onBlur={commitNewZone}
            />
          </div>
        )}
      </div>

      {/* Logística modal */}
      {showLogisticsModal && (
        <LogisticsModal
          checklist={checklist}
          transfers={transfers}
          links={links}
          items={items}
          onClose={() => setShowLogisticsModal(false)}
          onTransferCreated={() => {
            api.get(`/checklists/${checklistId}/logistics`).then(r => setTransfers(r.data));
            setShowLogisticsModal(false);
            refreshDispatchState();
          }}
        />
      )}

      {/* Note editor */}
      {noteTarget && (
        <NoteEditor
          zone={noteTarget.zone}
          category={noteTarget.category}
          existing={noteTarget.existing}
          onSave={handleNote}
          onClose={() => setNoteTarget(null)}
          onDelete={handleDeleteNote}
        />
      )}

      {/* Delete item confirm modal */}
      {deleteItemTarget && (
        <div className="modal-overlay" onClick={() => setDeleteItemTarget(null)}>
          <div className="modal" style={{ padding: '18px 20px', maxWidth: 340 }} onClick={e => e.stopPropagation()}>
            <p style={{ fontSize: 14, color: 'var(--text-dim)', marginBottom: 6 }}>¿Borrar equipo?</p>
            <p style={{ color: 'var(--yellow)', fontWeight: 600, fontSize: 15, marginBottom: 16 }}>
              {deleteItemTarget.name}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setDeleteItemTarget(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteItem}>
                Borrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset confirm modal */}
      {showResetConfirm && (
        <div className="modal-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">¿Reiniciar checklist?</h3>
            <p style={{ color: 'var(--text-dim)', fontSize: '14px', marginBottom: '24px' }}>
              Se borrarán todos los checks, reemplazos y notas. Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={() => setShowResetConfirm(false)}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  setShowResetConfirm(false);
                  await handleReset();
                }}
              >
                Sí, reiniciar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function EventSidebar({ currentId, navigate }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const fmt = d => d.toISOString().slice(0, 10);
    const from = new Date(); from.setDate(from.getDate() - 14);
    const to = new Date(); to.setDate(to.getDate() + 60);
    api.get(`/checklists/planning?from=${fmt(from)}&to=${fmt(to)}`)
      .then(r => setEvents(r.data.filter(e => e.checklist)))
      .catch(console.error);
  }, []);

  const byDate = useMemo(() => {
    const map = new Map();
    for (const ev of events) {
      const d = (ev.event_date || '').slice(0, 10);
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(ev);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  return (
    <div className="cl-sidebar">
      <div className="cl-sidebar-title">Eventos</div>
      <div className="cl-sidebar-scroll">
        {byDate.map(([date, evs]) => (
          <div key={date} className="cl-sidebar-day">
            <div className="cl-sidebar-day-label">{formatSidebarDate(date)}</div>
            {evs.map(ev => {
              const active = ev.checklist.id === currentId;
              return (
                <button
                  key={ev.id}
                  className={`cl-sidebar-event${active ? ' cl-sidebar-event--active' : ''}`}
                  onClick={() => navigate(`/checklist/${ev.checklist.id}`)}
                >
                  <div className="cl-sidebar-event-name">{ev.name}</div>
                  {ev.place && <div className="cl-sidebar-event-place">{ev.place}</div>}
                  {(ev.start_time || ev.end_time) && (
                    <div className="cl-sidebar-event-time">
                      {ev.start_time?.slice(0, 5)}
                      {ev.end_time && <> → {ev.end_time.slice(0, 5)}</>}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSidebarDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const days = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}

const FULL_DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
// Hora de comienzo de armado. Si es un día previo al evento, antepone el día.
function formatArmado(setupDate, setupStart, eventDate) {
  const t = String(setupStart).slice(0, 5);
  const sd = setupDate ? String(setupDate).slice(0, 10) : null;
  const ed = eventDate ? String(eventDate).slice(0, 10) : null;
  if (sd && ed && sd < ed) {
    const d = new Date(sd + 'T12:00:00');
    return `${FULL_DAYS[d.getDay()]} ${t}`;
  }
  return t;
}

function CheckItem({ item, canOperate, isCarga, itemLinks, transferOut, transferIn, alreadyDispatched = false, isActive, itemRef, onToggle, onConsolidate, onRename, onDelete, onActivate, conflictData, onConflictEventClick, committedIds, conflictsData, crossConflicts, eventId, linkCtx }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const editRef = useRef(null);

  const groupedLinks = useMemo(() => groupLogicalInventory(itemLinks), [itemLinks]);

  const removeLinks = async (toRemove) => {
    try {
      await Promise.all(toRemove.map(l => api.delete(`/links/checklist-item/${item.id}/${l.id}`)));
      const ids = new Set(toRemove.map(l => l.id));
      onConsolidate(itemLinks.filter(l => !ids.has(l.id)));
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (editing && editRef.current) {
      editRef.current.focus();
      // Cursor al final, no seleccionar todo: en el teléfono la selección
      // completa hace que el primer carácter que tocás borre el nombre entero,
      // y corregir una palabra obliga a reescribirlo.
      const fin = editRef.current.value.length;
      editRef.current.setSelectionRange(fin, fin);
    }
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    setEditValue(item.current_name);
    setEditing(true);
  };

  const commitEdit = () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      // Campo vacío: revertir al nombre original de planilla
      if (item.current_name !== item.original_name) onRename(item.original_name);
    } else if (trimmed !== item.current_name) {
      onRename(trimmed);
    }
    setEditing(false);
  };

  const cancelEdit = () => setEditing(false);

  const isInline = canOperate && isCarga;

  if (isInline) {
    return (
      <div
        ref={itemRef}
        className={`cl-item cl-item--grid${item.is_checked ? ' checked' : ''}${item.is_replaced && !item.is_manual ? ' replaced' : ''}${isActive ? ' cl-item--active' : ''}${alreadyDispatched ? ' cl-item--dispatched' : ''}`}
        onClick={!alreadyDispatched ? onActivate : undefined}
      >
        {/* Col 1: checkbox */}
        <button
          className={`cl-checkbox${(item.is_checked || alreadyDispatched) ? ' checked' : ''}`}
          onClick={onToggle}
          disabled={!canOperate || alreadyDispatched}
        >
          {(item.is_checked || alreadyDispatched) && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          )}
        </button>

        {/* Col 2: badge (clickeable) + buscador debajo */}
        <div className="cl-item-stock">
          <div
            className="cl-item-badges"
            onClick={e => { if (!alreadyDispatched) { e.stopPropagation(); setSearchOpen(o => !o); } }}
            title={alreadyDispatched ? undefined : 'Buscar en inventario'}
          >
            {groupedLinks.length > 0 ? (
              <div className="ic-chips">
                {groupedLinks.map((group) => (
                  <StockChip
                    key={group.key}
                    label={group.name}
                    group={group.items}
                    conflict={conflictsData?.by_name?.[group.name]}
                    onRemove={() => removeLinks(group.items)}
                    removable={!alreadyDispatched}
                    onEventClick={onConflictEventClick}
                  />
                ))}
              </div>
            ) : alreadyDispatched ? (
              <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 4, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 700 }}>
                Ya despachado
              </span>
            ) : (
              <span className="cl-item-no-stock">sin stock</span>
            )}
          </div>
          {!alreadyDispatched && (
            <InlineConsolidator
              checklistItemId={item.id}
              links={itemLinks}
              onLinksChange={onConsolidate}
              itemName={item.current_name}
              isActive={isActive}
              noChips
              collapsible
              open={searchOpen}
              onOpenChange={setSearchOpen}
              committedIds={committedIds}
              crossConflicts={crossConflicts}
              eventId={eventId}
            />
          )}
        </div>

        {/* Col 3: texto del pedido + responsable + borrar */}
        <div className="cl-item-info">
          {editing ? (
            <div className="cl-item-edit" onClick={e => e.stopPropagation()}>
              <input
                ref={editRef}
                className="cl-item-edit-input"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.stopPropagation(); commitEdit(); }
                  if (e.key === 'Escape') { e.stopPropagation(); cancelEdit(); }
                }}
                onBlur={commitEdit}
              />
              <span className="cl-item-edit-orig">Original: {item.original_name}</span>
            </div>
          ) : (
            <span
              className={item.is_replaced && !item.is_manual ? 'replaced-name' : 'item-name'}
              onClick={!alreadyDispatched ? startEdit : undefined}
              style={{ cursor: alreadyDispatched ? 'default' : 'text' }}
              title={alreadyDispatched ? undefined : 'Click para renombrar'}
            >
              {item.current_name}
            </span>
          )}
          {!editing && item.is_replaced && !item.is_manual && (
            <span className="original-name">↩ {item.original_name}</span>
          )}
          {linkCtx?.comes_from && <LinkStayBadge text={`Ya en el lugar — viene de ${linkCtx.comes_from.event_name}`} />}
          {(transferOut || transferIn) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
              {transferOut && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>
                  Transferido a {transferOut.location_name}
                </span>
              )}
              {transferIn && (
                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--blue-dim, #0c1a2e)', color: 'var(--blue, #60a5fa)', fontWeight: 600 }}>
                  Transferido desde {transferIn.location_name}
                </span>
              )}
            </div>
          )}
          {item.is_checked && item.checked_by_name && (
            <span className="cl-item-by">{item.checked_by_name}</span>
          )}
        </div>

        {/* Col 5: borrar (todos los items) */}
        <div className="cl-item-delete">
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(item.id, item.current_name); }}
            className="cl-item-delete-btn"
            title="Borrar equipo"
          >✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`cl-item${item.is_checked ? ' checked' : ''}${item.is_replaced && !item.is_manual ? ' replaced' : ''}`}>
      <button
        className={`cl-checkbox${item.is_checked ? ' checked' : ''}`}
        onClick={onToggle}
        disabled={!canOperate}
      >
        {item.is_checked && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20,6 9,17 4,12"/>
          </svg>
        )}
      </button>
      <div className="cl-item-content">
        <div className="cl-item-top">
          <span className={item.is_replaced && !item.is_manual ? 'replaced-name' : 'item-name'}>
            {item.current_name}
          </span>
          {item.is_checked && item.checked_by_name && (
            <span className="cl-item-by text-mute text-xs">{item.checked_by_name}</span>
          )}
        </div>
        {item.is_replaced && !item.is_manual && (
          <span className="original-name">↩ {item.original_name}</span>
        )}
        {(transferOut || transferIn) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
            {transferOut && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>
                Transferido a {transferOut.location_name}
              </span>
            )}
            {transferIn && (
              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--blue-dim, #0c1a2e)', color: 'var(--blue, #60a5fa)', fontWeight: 600 }}>
                Transferido desde {transferIn.location_name}
              </span>
            )}
          </div>
        )}
        {itemLinks.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--green-dim)', color: 'var(--green)', fontWeight: 600 }}>✓ Consolidado</span>
            {groupedLinks.map(group => (
              <span key={group.key} style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                {group.items.length} × {group.name}
              </span>
            ))}
          </div>
        )}
        {itemLinks.length === 0 && (
          <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--surface2)', color: 'var(--text-dim)', fontWeight: 500, marginTop: 4, display: 'inline-block' }}>
            Sin consolidar
          </span>
        )}
      </div>
    </div>
  );
}

// Proporciones de las 3 columnas de la descarga (compartidas con el header)
const DESC_COLS = { nombre: 1.6, carga: 0.9, descarga: 1.5 };

function DescargaHeader() {
  const th = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-dim)', padding: '2px 10px' };
  return (
    <div style={{ display: 'flex', marginBottom: 2 }}>
      <div style={{ ...th, flex: DESC_COLS.nombre }}>Ítem</div>
      <div style={{ ...th, flex: DESC_COLS.carga }}>Carga</div>
      <div style={{ ...th, flex: DESC_COLS.descarga }}>Descarga</div>
    </div>
  );
}

function DescargaItem({ item, canOperate, isChecked, onToggle, linkCtx }) {
  const hasLinks = item.links && item.links.length > 0;
  const isTransferredOut = !!item.transfer_out;
  const isAlreadyReceived = !!item.already_received;
  const isNotLoaded = !item.is_dischargeable;
  const isToggleable = canOperate && !isTransferredOut && !isAlreadyReceived && !isNotLoaded;
  const effectivelyChecked = isChecked && !isNotLoaded;

  // Agrupar links por nombre → "3 × Tacho 006" (mismo formato que carga),
  // en vez de listar cada unidad por separado.
  const groups = useMemo(() => {
    const map = new Map();
    for (const l of item.links || []) {
      const key = l.pack_name || l.item_name || '';
      map.set(key, (map.get(key) || 0) + 1);
    }
    return [...map.entries()];
  }, [item.links]);

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 2, overflow: 'hidden' }}>
      {/* Columna 1: NOMBRE (consolidado) */}
      <div style={{ flex: DESC_COLS.nombre, minWidth: 0, padding: '8px 10px', borderRight: '1px solid var(--border)' }}>
        {hasLinks ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 500, display: 'flex', flexWrap: 'wrap', columnGap: 8, rowGap: 2 }}>
              {groups.map(([label, count]) => (
                <span key={label}>
                  {count > 1 && <span style={{ fontWeight: 700, color: 'var(--green, #22c55e)' }}>{count} × </span>}
                  {label}
                </span>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{item.current_name}</div>
          </>
        ) : (
          <div style={{ fontSize: 13, fontWeight: 500 }}>{item.current_name}</div>
        )}
        {linkCtx?.stays_for && <LinkStayBadge text={`Se queda para ${linkCtx.stays_for.event_name} — no devolver`} />}
      </div>

      {/* Columna 2: CARGA (estado de la carga, tal cual) */}
      <div style={{ flex: DESC_COLS.carga, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRight: '1px solid var(--border)' }}>
        <div style={{
          width: 16, height: 16, borderRadius: 3, flexShrink: 0,
          border: '1.5px solid var(--border)',
          background: item.is_checked ? 'var(--green, #22c55e)' : undefined,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {item.is_checked && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          )}
        </div>
        {item.is_checked && item.checked_by_name && (
          <div style={{ fontSize: 10, color: 'var(--text-dim)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.checked_by_name}</div>
        )}
      </div>

      {/* Columna 3: DESCARGA (tal cual) */}
      <div
        onClick={isToggleable ? onToggle : undefined}
        style={{
          flex: DESC_COLS.descarga, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
          cursor: isToggleable ? 'pointer' : 'default',
          background: effectivelyChecked && !isAlreadyReceived ? 'var(--surface2)' : undefined,
          opacity: isNotLoaded ? 0.35 : isAlreadyReceived ? 0.55 : 1,
        }}
      >
        <div style={{
          width: 16, height: 16, borderRadius: 3, flexShrink: 0,
          border: '1.5px solid var(--border)',
          background: !isNotLoaded && isAlreadyReceived
            ? 'var(--text-mute, #555)'
            : effectivelyChecked ? 'var(--accent, #6366f1)' : undefined,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {effectivelyChecked && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {isTransferredOut && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>
              Transferido a {item.transfer_out.location_name}
            </span>
          )}
          {!isTransferredOut && item.transfer_in && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--blue-dim, #0c1a2e)', color: 'var(--blue, #60a5fa)', fontWeight: 600 }}>
              Transferido desde {item.transfer_in.location_name}
            </span>
          )}
          {isNotLoaded && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--surface2)', color: 'var(--text-dim)', fontWeight: 500 }}>
              No cargado
            </span>
          )}
          {!isNotLoaded && !hasLinks && !isTransferredOut && (
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'var(--yellow-dim, #3d2e00)', color: 'var(--yellow, #f59e0b)', fontWeight: 600 }}>
              Sin consolidar
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


const VEHICLES = [
  'Camion Grande',
  'Camion Mediano Azul',
  'Camion Mediano Rojo',
  'Camion Chico',
  'Fiorino Andres',
  'Fiorino Alallon',
  'Vehículo particular',
];

function groupItems(items) {
  const zones = [];
  const zoneIndex = new Map();

  for (const item of items) {
    const zoneKey = item.zone || '';
    if (!zoneIndex.has(zoneKey)) {
      zoneIndex.set(zoneKey, zones.length);
      zones.push({ zone: item.zone || null, categories: [] });
    }
    const zoneEntry = zones[zoneIndex.get(zoneKey)];

    const catKey = item.category || '';
    let catEntry = zoneEntry.categories.find(c => (c.category || '') === catKey);
    if (!catEntry) {
      catEntry = { category: item.category || null, items: [] };
      zoneEntry.categories.push(catEntry);
    }
    catEntry.items.push(item);
  }

  return zones;
}
